export type Role = "admin" | "client";
export type Stage = "brief" | "discovery" | "development" | "testing" | "golive";
export type ProjectStatus = "active" | "on_hold" | "complete" | "cancelled";

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  client_id: string | null;
}

export interface Client {
  id: string;
  name: string;
  created_at: number;
}

export interface ClientUser {
  id: string;
  email: string;
  name: string;
  created_at: number;
}

export interface Project {
  id: string;
  client_id: string;
  client_name: string;
  title: string;
  brief: string;
  stage: Stage;
  status: ProjectStatus;
  created_at: number;
  updated_at: number;
  // Present on list responses (GET /projects) only, not the single-project GET.
  open_ticket_count?: number;
}

export const STAGES: Stage[] = [
  "brief",
  "discovery",
  "development",
  "testing",
  "golive",
];

export const STAGE_LABELS: Record<Stage, string> = {
  brief: "Brief",
  discovery: "Discovery",
  development: "Development",
  testing: "Testing",
  golive: "Go Live",
};

export const STATUS_LABELS: Record<ProjectStatus, string> = {
  active: "Active",
  on_hold: "On hold",
  complete: "Complete",
  cancelled: "Cancelled",
};

export type TicketType = "issue" | "change";
export type TicketStatus = "open" | "in_progress" | "resolved" | "closed";
export type TicketPriority = "low" | "normal" | "high" | "urgent";

export interface Ticket {
  id: string;
  project_id: string;
  author_id: string | null;
  author_email: string | null;
  type: TicketType;
  title: string;
  body: string;
  status: TicketStatus;
  priority: TicketPriority;
  due_date: number | null;
  created_at: number;
  updated_at: number;
}

export interface TicketComment {
  id: string;
  ticket_id: string;
  author_id: string | null;
  author_email: string | null;
  body: string;
  // 1 = internal (admin-only); clients never receive these.
  internal: number;
  created_at: number;
}

// A ticket joined with its project's client/title — used by the dashboards.
export interface DashboardTicket extends Ticket {
  client_id: string;
  project_title: string;
}

export const TICKET_TYPES: TicketType[] = ["issue", "change"];
export const TICKET_STATUSES: TicketStatus[] = [
  "open",
  "in_progress",
  "resolved",
  "closed",
];
export const TICKET_PRIORITIES: TicketPriority[] = [
  "low",
  "normal",
  "high",
  "urgent",
];

export const TICKET_TYPE_LABELS: Record<TicketType, string> = {
  issue: "Issue",
  change: "Change request",
};
export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
};
export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
};

export type ScopeStatus = "planned" | "in_progress" | "done";

export interface ScopeItem {
  id: string;
  project_id: string;
  title: string;
  description: string;
  status: ScopeStatus;
  created_at: number;
  updated_at: number;
}

export interface StageContent {
  stage: Stage;
  content: string;
  updated_at: number;
}

export interface Attachment {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  created_at: number;
  uploaded_by_email: string | null;
}

export interface Credential {
  id: string;
  label: string;
  username: string;
  created_at: number;
  created_by_email: string | null;
}

export interface ActivityEvent {
  id: string;
  kind: string;
  detail: string; // JSON text
  created_at: number;
  actor_email: string | null;
}

export interface ChecklistItem {
  id: string;
  label: string;
  done: number; // 0 | 1
  created_at: number;
}

export type InternalPriority = "low" | "normal" | "high";

export interface InternalChecklistItem {
  id: string;
  label: string;
  note: string;
  priority: InternalPriority;
  done: number; // 0 | 1
  created_at: number;
  done_at: number | null;
}

export const INTERNAL_PRIORITIES: InternalPriority[] = ["low", "normal", "high"];
export const INTERNAL_PRIORITY_LABELS: Record<InternalPriority, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
};

export const SCOPE_STATUSES: ScopeStatus[] = ["planned", "in_progress", "done"];
export const SCOPE_STATUS_LABELS: Record<ScopeStatus, string> = {
  planned: "Planned",
  in_progress: "In progress",
  done: "Done",
};
